import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import LineItemsEditor, { computeTotals } from "@/components/LineItemsEditor";
import SearchableCustomerSelect from "@/components/SearchableCustomerSelect";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

export default function SalesReturnEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeCompanyId, companies, dateFloor } = useAuth();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const [customer, setCustomer] = useState(null);
  const [originalInvoiceId, setOriginalInvoiceId] = useState("");
  const [originalInvoiceNumber, setOriginalInvoiceNumber] = useState("");
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    (async () => {
      const [cs, ps, invs] = await Promise.all([
        api.get("/customers"),
        api.get("/products"),
        api.get("/sales-invoices"),
      ]);
      setCustomers(cs.data); setProducts(ps.data); setInvoices(invs.data);
      if (isEdit) {
        try {
          const { data } = await api.get(`/sales-returns/${id}`);
          setDate(data.date); setReason(data.reason || ""); setNotes(data.notes || "");
          setItems(data.items || []);
          setOriginalInvoiceId(data.original_invoice_id || "");
          setOriginalInvoiceNumber(data.original_invoice_number || "");
          const c = cs.data.find((x) => x.id === data.customer_id);
          setCustomer(c || { id: data.customer_id, name: data.customer_name, state_code: data.customer_state_code, gstin: data.customer_gstin });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
      }
    })();
  }, [activeCompanyId, id, isEdit]);

  const activeCompany = companies.find((c) => c.id === activeCompanyId);
  const intraState = customer && activeCompany && customer.state_code && activeCompany.state_code && customer.state_code === activeCompany.state_code;
  const totals = useMemo(() => computeTotals(items, !!intraState), [items, intraState]);

  const customerInvoices = invoices.filter((i) => !customer || i.customer_id === customer.id);

  // when an original invoice is picked, seed items from that invoice (only when creating)
  const pickInvoice = (inv_id) => {
    const inv = invoices.find((i) => i.id === inv_id);
    if (!inv) { setOriginalInvoiceId(""); setOriginalInvoiceNumber(""); return; }
    setOriginalInvoiceId(inv.id);
    setOriginalInvoiceNumber(inv.number);
    if (!isEdit) {
      // pre-fill items from that invoice
      setItems((inv.items || []).map((it) => ({
        product_id: it.product_id || "", name: it.name, hsn: it.hsn || "",
        quantity: it.quantity, unit: it.unit || "PCS",
        rate: it.rate, discount_pct: it.discount_pct || 0, tax_rate: it.tax_rate,
      })));
      if (!customer) {
        const c = customers.find((x) => x.id === inv.customer_id);
        if (c) setCustomer(c);
      }
    }
  };

  const save = async () => {
    if (!customer) { toast.error("Select a customer"); return; }
    if (items.length === 0) { toast.error("Add at least one line"); return; }
    setSaving(true);
    try {
      const payload = {
        date, reason, notes,
        customer_id: customer.id, customer_name: customer.name,
        customer_gstin: customer.gstin || "", customer_state_code: customer.state_code || "",
        original_invoice_id: originalInvoiceId, original_invoice_number: originalInvoiceNumber,
        items: items.map((it) => ({
          product_id: it.product_id || "", name: it.name, hsn: it.hsn || "",
          quantity: Number(it.quantity) || 0, unit: it.unit || "PCS",
          rate: Number(it.rate) || 0, discount_pct: Number(it.discount_pct) || 0,
          tax_rate: Number(it.tax_rate) || 0,
        })),
      };
      if (isEdit) {
        await api.patch(`/sales-returns/${id}`, payload);
        toast.success("Credit note updated");
      } else {
        await api.post("/sales-returns", payload);
        toast.success("Credit note created");
      }
      navigate("/returns");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
          </Button>
          <div>
            <div className="eyebrow">Sales</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {isEdit ? "Edit Credit Note" : "New Credit Note"}
            </h1>
          </div>
        </div>
      </div>

      <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Customer *</Label>
          <SearchableCustomerSelect
            value={customer?.id || ""}
            onChange={(v) => setCustomer(customers.find((c) => c.id === v))}
            customers={customers}
            placeholder="Search & choose customer"
            testid="return-customer"
            getSubtitle={(c) => c.gstin || c.phone}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Date</Label>
          <Input type="date" min={dateFloor} value={date} onChange={(e) => setDate(e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="eyebrow">Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger><SelectValue placeholder="Choose reason" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Damaged Goods">Damaged Goods</SelectItem>
              <SelectItem value="Wrong Item">Wrong Item</SelectItem>
              <SelectItem value="Quality Issue">Quality Issue</SelectItem>
              <SelectItem value="Excess Delivery">Excess Delivery</SelectItem>
              <SelectItem value="Price Correction">Price Correction</SelectItem>
              <SelectItem value="Customer Requested Cancellation">Customer Requested Cancellation</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Original Invoice (optional)</Label>
          <Select value={originalInvoiceId || "none"} onValueChange={(v) => pickInvoice(v === "none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Link to original invoice (auto-fills items)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {customerInvoices.map((i) => (
                <SelectItem key={i.id} value={i.id}>{i.number} · {i.date} · {inr(i.grand_total)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="eyebrow">Notes</Label>
          <Textarea rows={1} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>

      <LineItemsEditor items={items} setItems={setItems} products={products} intraState={!!intraState} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 card-flat p-6">
          <div className="eyebrow mb-2">Tax Treatment</div>
          <div className="text-sm">
            {intraState ? "Intra-state · CGST + SGST reversed" : "Inter-state · IGST reversed"}
          </div>
          <div className="text-[12px] text-muted-foreground mt-1">
            Company: {activeCompany?.state_code || "—"} · Customer: {customer?.state_code || "—"}
          </div>
        </div>
        <div className="card-flat p-6">
          <div className="eyebrow mb-3">Credit Summary</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Taxable</span><span className="font-mono">{inr(totals.subtotal)}</span></div>
            {intraState ? (
              <>
                <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="font-mono">{inr(totals.cgst_total)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="font-mono">{inr(totals.sgst_total)}</span></div>
              </>
            ) : (
              <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="font-mono">{inr(totals.igst_total)}</span></div>
            )}
            <div className="pt-2 border-t border-border flex justify-between font-heading font-bold text-base text-destructive">
              <span>Credit to Customer</span><span className="font-mono" data-testid="return-grand-total">{inr(totals.grand_total)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} className="h-11 px-6 text-base" data-testid="return-save-btn">
          <FloppyDisk size={18} className="mr-1.5" /> {saving ? "Saving…" : "Save Credit Note"}
        </Button>
      </div>
    </div>
  );
}
